import {useCallback, useEffect, useState} from "react";
import classNames from "classnames";
import colors from "styles/themes.module.scss";
import {useAppDispatch, useModalHook} from "hooks";
import {Button, Form, ModalContent, ModalHeader, ModalSeparator} from "components";

import {TTwoFactorAuthModalProps} from "./types";
import styles from "./styles.module.scss";
import {
	disable2FAAuth,
	enable2FAAuth,
	get2FAStatus,
	verifyAndEnable2FAAuth,
} from "store/user/user2FAAuthAsyncActions";
import twoFAAuthForm from "mapx-constants/forms/twoFAAuthForm";
import {ST2StepEnabledResponse, ST2StepVerifyNEnabledResponse} from "api/authApi/types";
import codeFromAuthenticatorAppForm from "mapx-constants/forms/twoFAAuthForm/verifyAndEnableForm";
import verifyNDisable from "mapx-constants/forms/twoFAAuthForm/verifyNDisable";
import {TErrorResponse} from "types";
import CustomConfirmAlert from "helpers/CustomConfirmAlert";

const TwoFactorAuthModal = ({
	twoFactorAuthModalDisplay = true,
	setTwoFactorAuthModalDisplay,
}: TTwoFactorAuthModalProps) => {
	const dispatch = useAppDispatch();

	const {loading, setLoading, Modal, customStyles} = useModalHook(
		{content: {width: "99%", borderRadius: "8px", maxWidth: "720px"}},
		twoFactorAuthModalDisplay,
		colors.mainThemeColor,
	);

	const [current2FAStatusIsActive, setCurrent2FaStatusIsActive] = useState<boolean>(false);

	const [formError, setFormError] = useState<string>("");

	const [passwordValidated, setPasswordValidated] = useState<boolean>(false);

	const [qrCode, setQrCode] = useState<Nullable<string>>(null);

	const [secret, setSecret] = useState<Nullable<string>>(null);

	const [backupCodes, setBackupCodes] = useState<string[]>([]);

	const closeModal = () => {
		if (passwordValidated && Array.isArray(backupCodes) && backupCodes.length > 0) {
			CustomConfirmAlert({
				yesButtonLabel: "Yes, I've copied the code.",
				noButtonLabel: "Back",
				title: `Are you sure?`,
				message: `If you lose access to your account and don't have these codes, it can't be recovered.`,
				handlePressYes: async () => {
					setTwoFactorAuthModalDisplay(false);
					setPasswordValidated(false);
				},
				alertType: "warning",
				noButtonStyle: {minWidth: "100px", width: "30%"},
				yesButtonStyle: {minWidth: "160px", width: "70%"},
			});
		} else {
			setTwoFactorAuthModalDisplay(false);
			setPasswordValidated(false);
		}
	};

	const onSubmit = useCallback(
		async (values: {currentPassword: string}) => {
			setLoading(true);

			if (!current2FAStatusIsActive) {
				const response = await dispatch(
					enable2FAAuth({
						password: values.currentPassword,
					}),
				);

				if (response !== null) {
					if ((response as unknown as TErrorResponse)?.error) {
						setFormError((response as unknown as TErrorResponse)?.error);
					} else {
						setPasswordValidated(true);
						setQrCode((response as unknown as ST2StepEnabledResponse).qr_code);
						setSecret((response as unknown as ST2StepEnabledResponse).secret);
						setFormError("");
					}
				}
			}

			setLoading(false);
		},
		[dispatch, current2FAStatusIsActive, setLoading],
	);

	const onDisableSubmit = useCallback(
		async (values: {currentPassword: string; code: string}) => {
			setLoading(true);

			if (current2FAStatusIsActive) {
				const response = await dispatch(
					disable2FAAuth({
						password: values.currentPassword,
						code: values.code,
					}),
				);

				if (response !== null) {
					if ((response as unknown as TErrorResponse)?.error) {
						setFormError((response as unknown as TErrorResponse)?.error);
					} else {
						setPasswordValidated(false);
						setQrCode(null);
						setSecret(null);
						setBackupCodes([]);
						setCurrent2FaStatusIsActive(false);
						setFormError("");
					}
				}
			}

			setLoading(false);
		},
		[dispatch, current2FAStatusIsActive, setLoading],
	);

	const onVerifyNSubmit = useCallback(
		async (values: {code: number}) => {
			setLoading(true);

			if (String(values.code).length === 6) {
				const response = await dispatch(
					verifyAndEnable2FAAuth({
						code: String(values.code),
						secret: secret as string,
					}),
				);

				if (response !== null) {
					if ((response as unknown as TErrorResponse)?.error) {
						setFormError((response as unknown as TErrorResponse)?.error);
					} else {
						setCurrent2FaStatusIsActive(true);
						setBackupCodes(
							(response as unknown as ST2StepVerifyNEnabledResponse).backup_codes,
						);
						setFormError("");
					}
				}
			}

			setLoading(false);
		},
		[dispatch, secret, setLoading],
	);

	const get2FAStatusFromAPI = useCallback(async () => {
		const response = await dispatch(get2FAStatus());

		if (response && "enabled" in response) {
			setCurrent2FaStatusIsActive(response.enabled as boolean);
		} else {
			setCurrent2FaStatusIsActive(false);
		}
	}, [dispatch]);

	useEffect(() => {
		if (twoFactorAuthModalDisplay) {
			get2FAStatusFromAPI();
		}
	}, [get2FAStatusFromAPI, twoFactorAuthModalDisplay]);

	const copyBackupCodesToClipboard = async () => {
		try {
			if (!navigator.clipboard) {
				throw new Error("Clipboard API not supported");
			}

			const textToCopy = backupCodes.join(", \n");
			await navigator.clipboard.writeText(textToCopy);

			const copyButton = document.querySelector(`.${styles.wrapper__backup_codes__copy}`);

			if (copyButton) {
				const originalText = copyButton.textContent;
				copyButton.textContent = "Copied!";
				setTimeout(() => {
					copyButton.textContent = originalText;
				}, 2000);
			}
		} catch (error) {
			console.error("Failed to copy backup codes:", error);
			// Optional: Show error feedback to the user
			alert("Failed to copy backup codes. Please try again or copy them manually.");
		}
	};

	return (
		<Modal
			style={customStyles}
			title="Add from Target List"
			isOpen={twoFactorAuthModalDisplay}
			contentLabel="Modal"
			onRequestClose={closeModal}
			className={classNames("modal", styles.wrapper, {
				[styles.wrapper__changed]: current2FAStatusIsActive,
			})}
		>
			{/*{current2FAStatusIsActive && <ChangeApprovedIcon className={styles.wrapper__changed_icon}/>}*/}

			<ModalHeader
				title={
					passwordValidated && Array.isArray(backupCodes) && backupCodes.length > 0
						? "Backup codes (one-time view)"
						: !current2FAStatusIsActive
						? "Enable 2-Step Verification"
						: "Disable 2-Step Verification"
				}
				onClose={closeModal}
			/>

			<ModalSeparator />

			<ModalContent padding="16px 16px 32px 16px" className={styles.content}>
				{!passwordValidated && (
					<h3
						className={classNames(styles.wrapper__subtitle, {
							[styles.wrapper__changed_subtitle]: passwordValidated,
						})}
					>
						{`Please enter your password and click on the ${
							current2FAStatusIsActive ? "disable" : "enable"
						} button`}
					</h3>
				)}

				{!passwordValidated && !current2FAStatusIsActive && (
					<Form
						loading={loading}
						onSubmit={onSubmit}
						form={twoFAAuthForm}
						inputError={formError}
						setInputError={setFormError}
						submitText="Enable"
						className={styles.wrapper__form}
						btnClasses={styles.wrapper__form__btn}
					/>
				)}

				{passwordValidated && !current2FAStatusIsActive && qrCode && (
					<div className={styles.wrapper__qr_code}>
						<h3 className={classNames(styles.wrapper__subtitle)}>
							Scan the QR code with your authenticator app, then verify with a code to
							enable 2FA
						</h3>

						<img src={qrCode} alt="2FA QR" width={128} />

						<Form
							loading={loading}
							onSubmit={onVerifyNSubmit}
							form={codeFromAuthenticatorAppForm}
							inputError={formError}
							setInputError={setFormError}
							submitText="Verify & Enable"
							className={styles.wrapper__form}
							btnClasses={styles.wrapper__form__btn}
						/>
					</div>
				)}

				{passwordValidated && Array.isArray(backupCodes) && backupCodes.length > 0 && (
					<div className={styles.wrapper__backup_codes}>
						<ul>
							{backupCodes.map((c, i) => (
								<li key={i}>{c}</li>
							))}
						</ul>
						<span
							className={styles.wrapper__backup_codes__copy}
							onClick={copyBackupCodesToClipboard}
						>
							Copy to clipboard
						</span>

						<span>
							<b>Note: </b>Store these codes securely (e.g., password manager). They
							can be used when you lose access to your authenticator app.
						</span>

						<Button type="button" onClick={closeModal} className={styles.doneButton}>
							Done
						</Button>
					</div>
				)}

				{!passwordValidated && current2FAStatusIsActive && (
					<Form
						loading={loading}
						onSubmit={onDisableSubmit}
						form={verifyNDisable}
						inputError={formError}
						setInputError={setFormError}
						submitText="Disable"
						className={styles.wrapper__form}
						btnClasses={styles.wrapper__form__btn}
					/>
				)}
			</ModalContent>
		</Modal>
	);
};

TwoFactorAuthModal.displayName = "TwoFactorAuthModal";

export default TwoFactorAuthModal;
